import { Component } from '@theme/component';

/**
 * Mini product card. Handles variant selection: updates the displayed price and
 * syncs the child <add-to-cart-button-ajax> with the selected variant. If the
 * add button is clicked before a size is chosen it dispatches
 * `add-to-cart-ajax:no-variant`, which surfaces the error below the selector.
 */
class MiniProductCard extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('add-to-cart-ajax:no-variant', this.#showVariantError);
    // Keep the size dropdown's pointer interaction from reaching the parent
    // <drag-scroll> — otherwise opening the native select starts a drag (Safari).
    if (this.refs.variantSelect instanceof HTMLSelectElement) {
      this.refs.variantSelect.addEventListener('pointerdown', this.#stopDragPropagation);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('add-to-cart-ajax:no-variant', this.#showVariantError);
    this.refs.variantSelect?.removeEventListener('pointerdown', this.#stopDragPropagation);
  }

  #showVariantError = () => {
    if (this.refs.variantError) this.refs.variantError.hidden = false;
  };

  /** @param {Event} event */
  #stopDragPropagation = (event) => {
    event.stopPropagation();
  };

  /** @param {Event} event */
  handleVariantChange(event) {
    const select = this.refs.variantSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    // The first option is a disabled "Size" placeholder (empty value).
    const isPlaceholder = option.value === '';

    if (!isPlaceholder && this.refs.variantError) {
      this.refs.variantError.hidden = true;
    }

    // Prices are pre-formatted server-side (money filter) so they're correct
    // per market — never format money on the client.
    if (!isPlaceholder && this.refs.salePrice) {
      this.refs.salePrice.textContent = option.dataset.price ?? '';
    }

    if (!isPlaceholder) {
      const onSale = option.dataset.onSale === 'true';

      if (this.refs.price) {
        this.refs.price.classList.toggle('mini-product-card__price--on-sale', onSale);
      }

      if (this.refs.comparePrice) {
        if (onSale) {
          this.refs.comparePrice.textContent = option.dataset.comparePrice ?? '';
          this.refs.comparePrice.style.display = '';
        } else {
          this.refs.comparePrice.style.display = 'none';
        }
      }
    }

    // Intelligems: point the tagged price nodes at the chosen size and let IG re-price them,
    // since the text above was just reset to the base price.
    if (!isPlaceholder) {
      for (const node of [this.refs.salePrice, this.refs.comparePrice]) {
        if (node instanceof HTMLElement) node.dataset.variantId = option.value;
      }
      /** @type {any} */ (window).igData?.resetDom?.();
    }

    const addButton = this.querySelector('add-to-cart-button-ajax');
    if (addButton && typeof addButton.setVariant === 'function') {
      addButton.setVariant(isPlaceholder ? '' : option.value, !isPlaceholder && !option.disabled);
    }
  }
}

if (!customElements.get('mini-product-card')) {
  customElements.define('mini-product-card', MiniProductCard);
}
